import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { DollarSign, Loader2, CreditCard, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getStripeErrorMessage } from '@/lib/stripeErrorMessages';

interface ChargeAdhocModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  dealName?: string;
  contactName?: string;
  hasCard?: boolean;
  onSuccess?: () => void;
  onGenerateLink?: (amount: number, description: string) => Promise<void>;
}

export function ChargeAdhocModal({
  open,
  onOpenChange,
  dealId,
  dealName,
  contactName,
  hasCard = false,
  onSuccess,
  onGenerateLink
}: ChargeAdhocModalProps) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isCharging, setIsCharging] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount('');
      setDescription(dealName || '');
    }
  }, [open, dealName]);

  const formatCurrency = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (!numbers) return '';
    const cents = parseInt(numbers, 10);
    return (cents / 100).toFixed(2);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrency(e.target.value);
    setAmount(formatted);
  };

  const numericAmount = parseFloat(amount) || 0;
  const isValid = numericAmount > 0;

  const handleChargeNow = async () => {
    if (!isValid) return;
    
    setIsCharging(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase.functions.invoke('stripe-charge-adhoc', {
        body: {
          deal_id: dealId,
          amount: numericAmount,
          description: description || dealName || 'Cobrança avulsa',
          triggered_by: user?.id
        }
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('✅ Cobrança realizada com sucesso!');
        onOpenChange(false);
        onSuccess?.();
      } else if (data?.no_card) {
        toast.error('Cliente não possui cartão salvo');
      } else if (data?.no_customer) {
        toast.error('Cliente não possui Stripe ID');
      } else if (data?.requires_action) {
        toast.warning('Cartão requer autenticação. Use o link de pagamento.');
      } else if (data?.failed) {
        const friendlyMessage = getStripeErrorMessage(data.error_code, data.message);
        toast.error(`Falha na cobrança: ${friendlyMessage}`);
      } else {
        toast.error(data?.message || 'Erro ao processar cobrança');
      }
    } catch (err: any) {
      console.error('Charge error:', err);
      toast.error('Erro ao cobrar: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsCharging(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!isValid || !onGenerateLink) return;
    
    setIsGeneratingLink(true);
    try {
      await onGenerateLink(numericAmount, description);
      onOpenChange(false);
    } catch (err: any) {
      console.error('Generate link error:', err);
      toast.error('Erro ao gerar link: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const isLoading = isCharging || isGeneratingLink;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Cobrança
          </DialogTitle>
          <DialogDescription>
            {contactName ? `Cobrar ${contactName}` : 'Insira o valor a cobrar'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Valor (USD)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                id="amount"
                type="text"
                inputMode="numeric"
                placeholder="0.00"
                value={amount}
                onChange={handleAmountChange}
                className="pl-8 text-lg font-medium"
                autoFocus
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Input
              id="description"
              type="text"
              placeholder={dealName || 'Ex: Pagamento de serviço'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {hasCard && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-950 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <CreditCard className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700 dark:text-emerald-300">
                Cliente possui cartão salvo
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          
          {onGenerateLink && (
            <Button
              type="button"
              variant="outline"
              onClick={handleGenerateLink}
              disabled={!isValid || isLoading}
              className="w-full sm:w-auto gap-2"
            >
              {isGeneratingLink ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              Gerar Link
            </Button>
          )}
          
          {hasCard && (
            <Button 
              type="button"
              onClick={handleChargeNow}
              disabled={!isValid || isLoading}
              className="w-full sm:w-auto gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {isCharging ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              Cobrar Agora
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
